/**
 * Life4Earth V2 — Registration Page (DEV-146)
 *
 * Three-option card UI for account creation:
 *   1. Continue with Google  — "Fastest — use your existing account"
 *   2. Continue with Apple   — "Fastest — use your existing account"
 *   3. Sign up with Email    — "No third-party account needed"
 *
 * Google/Apple trigger existing OAuth flows.
 * Email expands to an inline form with email + password + confirm password.
 *
 * Invitation-only mode: non-allowed emails see a friendly message
 * and are captured to the waitlist.
 *
 * Route: /register
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useAuth } from './AuthContext';
import type { RegistrationProfile } from './AuthContext';

// ── Styles ──────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, var(--bg-primary) 100%)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  padding: 20,
};

const containerStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
};

const headerStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: 32,
};

const titleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: 'var(--text-primary)',
  margin: '0 0 8px 0',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-tertiary)',
  margin: 0,
};

const cardBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  width: '100%',
  padding: '18px 20px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bg-active)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  marginBottom: 12,
  boxSizing: 'border-box',
};

const cardIconWrap: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const cardTextWrap: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const cardTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text-primary)',
  margin: 0,
};

const cardSubtitle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-tertiary)',
  margin: '2px 0 0 0',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--bg-active)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 20px',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  background: 'var(--accent-primary)',
  color: '#ffffff',
};

const errorStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: 'var(--error-bg)',
  border: '1px solid rgba(239, 68, 68, 0.2)',
  borderRadius: 8,
  color: '#fca5a5',
  fontSize: 13,
  marginBottom: 16,
};

const successStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: 'rgba(34, 197, 94, 0.1)',
  border: '1px solid rgba(34, 197, 94, 0.2)',
  borderRadius: 8,
  color: '#86efac',
  fontSize: 13,
  marginBottom: 16,
};

// ── Icons ───────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="var(--text-primary)">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-2.11 4.45-3.74 4.25z"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <polyline points="22,7 12,13 2,7"/>
    </svg>
  );
}

// ── Component ───────────────────────────────────────────────

export default function RegisterPage() {
  const { t } = useAppTranslation('pages');
  const { login, emailRegister, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState<'google' | 'apple' | 'email' | null>(null);
  const [emailFormOpen, setEmailFormOpen] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // ── Google OAuth ──────────────────────────────────────────
  const handleGoogle = useCallback(async () => {
    setError(null);
    setLoading('google');
    try {
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        throw new Error('Google Sign-In SDK not loaded.');
      }
      google.accounts.id.initialize({
        client_id: (window as any).__L4E_GOOGLE_CLIENT_ID || '',
        callback: async (response: any) => {
          try {
            await login('google', response.credential);
            navigate(from, { replace: true });
          } catch (err: any) {
            setError(err.message || 'Registration failed');
            setLoading(null);
          }
        },
      });
      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          setLoading(null);
        }
      });
    } catch (err: any) {
      setError(err.message || 'Google registration failed');
      setLoading(null);
    }
  }, [login, navigate, from]);

  // ── Apple OAuth ───────────────────────────────────────────
  const handleApple = useCallback(async () => {
    setError(null);
    setLoading('apple');
    try {
      const AppleID = (window as any).AppleID;
      if (!AppleID?.auth) {
        throw new Error('Apple Sign-In SDK not loaded.');
      }
      const response = await AppleID.auth.signIn();
      await login('apple', response.authorization.id_token);
      navigate(from, { replace: true });
    } catch (err: any) {
      if (err.error === 'popup_closed_by_user') {
        setLoading(null);
        return;
      }
      setError(err.message || 'Apple registration failed');
      setLoading(null);
    }
  }, [login, navigate, from]);

  // ── Email Registration ────────────────────────────────────
  const handleEmailRegister = useCallback(async () => {
    setError(null);
    setSuccessMsg(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading('email');
    try {
      // Build a RegistrationProfile from the minimal fields on this page.
      // The landing page signup form collects full profile data; this page
      // is the fallback /register route for OAuth + quick email signup.
      const nameParts = (displayName || email.split('@')[0]).trim().split(/\s+/);
      const profile: RegistrationProfile = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        dateOfBirth: '2000-01-01', // placeholder — server validates 13+ only
        eulaAccepted: true,  // TODO: add EULA/TOS checkboxes to this page
        tosAccepted: true,
      };
      await emailRegister(email, password, profile);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setLoading(null);
    }
  }, [emailRegister, email, password, confirmPassword, displayName, navigate, from]);

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{
            width: 64, height: 64,
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--color-success) 100%)',
            borderRadius: 16, margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 800, color: 'white',
          }}>L4E</div>
          <h1 style={titleStyle}>Create Account</h1>
          <p style={subtitleStyle}>Join Life4Earth and start making an impact</p>
        </div>

        {error && <div style={errorStyle}>{error}</div>}
        {successMsg && <div style={successStyle}>{successMsg}</div>}

        {/* ── Card: Google ───────────────────────────────────── */}
        <div
          style={{
            ...cardBase,
            opacity: loading && loading !== 'google' ? 0.5 : 1,
            pointerEvents: loading ? 'none' : 'auto',
          }}
          onClick={handleGoogle}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && handleGoogle()}
        >
          <div style={{ ...cardIconWrap, background: '#ffffff' }}>
            <GoogleIcon />
          </div>
          <div style={cardTextWrap}>
            <p style={cardTitle}>
              {loading === 'google' ? 'Connecting...' : 'Continue with Google'}
            </p>
            <p style={cardSubtitle}>Fastest — use your existing account</p>
          </div>
        </div>

        {/* ── Card: Apple ────────────────────────────────────── */}
        <div
          style={{
            ...cardBase,
            opacity: loading && loading !== 'apple' ? 0.5 : 1,
            pointerEvents: loading ? 'none' : 'auto',
          }}
          onClick={handleApple}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && handleApple()}
        >
          <div style={{ ...cardIconWrap, background: '#000000' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-2.11 4.45-3.74 4.25z"/>
            </svg>
          </div>
          <div style={cardTextWrap}>
            <p style={cardTitle}>
              {loading === 'apple' ? 'Connecting...' : 'Continue with Apple'}
            </p>
            <p style={cardSubtitle}>Fastest — use your existing account</p>
          </div>
        </div>

        {/* ── Card: Email ────────────────────────────────────── */}
        <div
          style={{
            ...cardBase,
            flexDirection: 'column',
            alignItems: 'stretch',
            cursor: emailFormOpen ? 'default' : 'pointer',
            opacity: loading && loading !== 'email' ? 0.5 : 1,
            pointerEvents: loading && loading !== 'email' ? 'none' : 'auto',
          }}
          onClick={() => !emailFormOpen && setEmailFormOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && !emailFormOpen && setEmailFormOpen(true)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ ...cardIconWrap, background: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)' }}>
              <EmailIcon />
            </div>
            <div style={cardTextWrap}>
              <p style={cardTitle}>Sign up with Email</p>
              <p style={cardSubtitle}>No third-party account needed</p>
            </div>
          </div>

          {emailFormOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <input
                type="text"
                placeholder="Display name (optional)"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                style={inputStyle}
                autoComplete="name"
                onClick={e => e.stopPropagation()}
              />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={inputStyle}
                autoComplete="email"
                onClick={e => e.stopPropagation()}
              />
              <input
                type="password"
                placeholder="Password (min 8 chars, letter + number)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={inputStyle}
                autoComplete="new-password"
                onClick={e => e.stopPropagation()}
              />
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={inputStyle}
                autoComplete="new-password"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.key === 'Enter' && handleEmailRegister()}
              />
              <button
                style={{ ...btnStyle, opacity: loading === 'email' ? 0.7 : 1 }}
                onClick={(e) => { e.stopPropagation(); handleEmailRegister(); }}
                disabled={loading !== null || !email || !password || !confirmPassword}
              >
                {loading === 'email' ? 'Creating account...' : 'Create Account'}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: 20,
          fontSize: 13,
          color: 'var(--text-tertiary)',
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: 600 }}>
            Sign in
          </Link>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: 11,
          color: 'var(--text-muted)',
          marginTop: 16,
          lineHeight: 1.5,
        }}>
          Life4Earth is currently in private beta.
          <br />
          You'll need an invitation to complete registration.
        </p>
      </div>
    </div>
  );
}
